import {Box, Typography} from '@mui/material';
import {useEffect, useState} from 'react';
import {getProjectTableColumnDef} from './projectTableColumnDef';
import {IProjectTableData} from './types';
import AurLoadingIndicator from '../../common/aur-loading-indicator/AurLoadingIndictor';
import {
  dismissNewProjects,
  getFilteredProjectList,
  getNewPendingProjects,
  getPermissionsbyClientId,
  getProjectsListXLSX,
} from '../../../api/ClientManagementService';
import {useDispatch, useSelector} from 'react-redux';
import {RootState} from '../../../redux/store';
import AurProjectListTableHeader from './AurProjectListTableHeader';
import {
  updateSelectedProjectId,
  setProjectTableFilterState,
  updateSelectedProjectIndex,
} from '../../../redux/slices/clientDetailsSlice';
import {t} from 'i18next';
import AurDataGridPro from '../../common/aur-data-grid-pro/AurDataGridPro';
import useServerSideDataGrid, {
  AurDataGridFilterState,
  GetTableData,
  toODataQuery,
} from '../../common/aur-data-grid-pro/useServerSideDataGrid';
import AurDataGridNoRowsOverlay from '../../common/aur-data-grid-no-rows-overlay/AurDataGridNoRowsOverlay';
import {
  MANAGE_PROJECT_PERMISSION_CODES_CONSTANTS,
  NEW_PENDING_PROJECTS_PAGE_SIZE,
  PERMISSION_TYPE,
  TABLE_DEFAULT_PAGE_NUMBER,
  TABLE_PAGE_SIZE_OPTION,
} from '../../../constants/constants';
import {GridRowParams} from '@mui/x-data-grid-pro';
import {useNavigate} from 'react-router-dom';
import navConstants from '../../../constants/navConstants';
import {
  downloadResponseAsXLSX,
  getFilenameFromHeaders,
  isEmptyInstance,
} from '../../../utils/utils';
import {
  PROJECT_CREATION_PATH,
  setProjectCreationInitiatedFrom,
  storeSelectedAgreement,
  setClientIdForProjectCreation,
} from '../../../redux/slices/projectCreationSlice';
import AurProjectStatusRow from '../../pages/project-creation/components/AurProjectStatusRow';
import AurTooltip from '../../common/aur-tooltip/AurTooltip';
import {ReactComponent as InfoIcon} from '../../../../src/assests/svg/export_name_icon.svg';
import {useToast} from '../../../contexts/toastContext/ToastContext';
import {showErrorToast} from '../../../api/utils';
import {Permission} from '../../../types/types';
import {Project} from '../../pages/welcome-screen-single-client/model';

const defaultFilterState: AurDataGridFilterState = {
  searchState: {
    items: [],
  },
  sortState: [{field: 'projectName', sort: 'asc'}],
  paginationState: {
    page: 1,
    pageSize: 25,
  },
};
const stringPath = 'common.project.';
const AurProjectListContainer = () => {
  const dispatch = useDispatch();
  const clientId = useSelector(
    (state: RootState) => state.clientList.selectedClientId
  );
  const navigate = useNavigate();
  const [projectList, setProjectList] = useState<Project[]>([]);
  const {showToast} = useToast();
  const [isRecentProjectsLoading, setIsRecentProjectsLoading] = useState(false);
  const [isDismissProjectLoading, setIsDismissProjectLoading] = useState(false);
  const [hasCreateProjectsPermission, setHasCreateProjectsPermission] =
    useState<boolean>(false);

  const getFilteredProjects: GetTableData<IProjectTableData> = async (
    filterState: AurDataGridFilterState
  ) => {
    try {
      const oDataQuery = toODataQuery(
        filterState.searchState,
        filterState.sortState,
        filterState.paginationState
      );
      const result = await getFilteredProjectList(clientId, oDataQuery);
      return {
        data: result?.data?.data?.data || [],
        totalCount: result?.data?.data?.totalCount || 0,
      };
    } catch (e) {
      console.error('Error fetching workspaces:', e);
      return {data: [], totalCount: 0};
    }
  };

  const {totalRowCount, rowsData, filterState, isLoading, serverSideEvents} =
    useServerSideDataGrid<IProjectTableData>(
      getFilteredProjects,
      defaultFilterState
    );

  const chevronClickHandler = (rowIndex: number) => {
    dispatch(updateSelectedProjectIndex(rowIndex + 1));
  };
  const handleRowClick = (rowId: string) => {
    const rowIndex = rowsData.findIndex((row) => row.projectId === rowId);
    chevronClickHandler(rowIndex);
  };

  const handleCreateNewProject = () => {
    dispatch(
      setProjectCreationInitiatedFrom(PROJECT_CREATION_PATH.ClientDetails)
    );
    dispatch(storeSelectedAgreement(undefined));
    dispatch(setClientIdForProjectCreation(clientId));
    navigate(navConstants.projectCreation.serviceAgreement);
  };

  const handleProjectListExportCSV = async () => {
    if (clientId) {
      try {
        const response = await getProjectsListXLSX(clientId);
        const fileName =
          getFilenameFromHeaders(response.headers['content-disposition']) ??
          `${clientId}_${Date.now()}`;
        downloadResponseAsXLSX(response, fileName, false);
      } catch (error) {
        console.error('Error exporting CSV:', error);
      }
    }
  };

  const fetchPermissions = async () => {
    if (isEmptyInstance(clientId)) {
      return;
    }
    try {
      const response = await getPermissionsbyClientId(clientId);
      const permissions = (response?.data?.data?.value?.permissions ||
        []) as Permission[];
      const manageProjectPermissions = permissions.filter(
        (item) => item?.type === PERMISSION_TYPE.MANAGE_PROJECT
      );

      const hasCreateProjectPermission = manageProjectPermissions.some(
        (item) =>
          item.type === PERMISSION_TYPE.MANAGE_PROJECT &&
          item.permissionCode ===
            MANAGE_PROJECT_PERMISSION_CODES_CONSTANTS.CREATE_NEW_PROJECT
      );
      setHasCreateProjectsPermission(hasCreateProjectPermission);
    } catch (error) {
      showErrorToast(showToast, error);
    }
  };

  const fetchNewPendingProjects = async () => {
    if (isEmptyInstance(clientId)) {
      return;
    }
    try {
      setIsRecentProjectsLoading(true);

      const params = {
        page: TABLE_DEFAULT_PAGE_NUMBER,
        pageSize: NEW_PENDING_PROJECTS_PAGE_SIZE,
      };
      const response = await getNewPendingProjects(clientId, params);
      const projects = response?.data?.data?.projects || [];
      setProjectList(projects);
    } catch (error) {
      showErrorToast(showToast, error, 'center');
      setProjectList([]);
    } finally {
      setIsRecentProjectsLoading(false);
    }
  };

  useEffect(() => {
    fetchPermissions();
    fetchNewPendingProjects();
  }, [clientId]);

  useEffect(() => {
    dispatch(setProjectTableFilterState(filterState));
  }, [filterState]);

  // reset selected project state
  useEffect(() => {
    dispatch(updateSelectedProjectIndex(-1));
    dispatch(updateSelectedProjectId(''));
  }, []);

  const handleProjectRemove = async (projectId: string) => {
    try {
      setIsDismissProjectLoading(true);
      const res = await dismissNewProjects(clientId, projectId);
      setProjectList(res.data?.data?.projects ?? []);
    } catch (error) {
      showErrorToast(showToast, error, 'center');
    } finally {
      setIsDismissProjectLoading(false);
    }
  };
  return (
    <Box className="client-details-project-list-main-conatiner">
      <Box>
        <AurProjectListTableHeader
          hasCreateProjectPermission={hasCreateProjectsPermission}
          projectListExportCSVHandler={handleProjectListExportCSV}
          createNewProjectHandler={handleCreateNewProject}
        />
      </Box>
      {(projectList.length > 0 || isRecentProjectsLoading) && (
        <Box className="mb-32">
          <Box className="project-list-header">
            <Typography className="typo-text body2_size">
              {t(stringPath + 'recentlyAddedTitle')}
            </Typography>
            <AurTooltip
              className="typo-text label3_size"
              title={t(stringPath + 'recentlyAddedTooltipInfo')}
              tabIndex={0}
            >
              <InfoIcon />
            </AurTooltip>
          </Box>
          {isRecentProjectsLoading && (
            <AurLoadingIndicator show={isRecentProjectsLoading} />
          )}
          {isDismissProjectLoading && (
            <AurLoadingIndicator
              isOpen={isDismissProjectLoading}
              overlay={true}
            />
          )}
          {projectList.map((project: Project) => (
            <AurProjectStatusRow
              key={`project-row-${project.projectId}`}
              projectDetails={project}
              onProjectRemoveClick={() =>
                handleProjectRemove(project.projectId)
              }
            />
          ))}
        </Box>
      )}
      <Typography
        tabIndex={0}
        className="typo-text body2_size project-sub-text"
      >
        {t('clientDetailsPage.projectTable.projectsSubText')}
      </Typography>
      <Box>
        {isLoading && <AurLoadingIndicator show={isLoading} />}

        <AurDataGridPro
          className="project-list-grid"
          rows={rowsData}
          columns={getProjectTableColumnDef(chevronClickHandler)}
          pageSizeOptions={TABLE_PAGE_SIZE_OPTION}
          entityType={t('common.projectsPagination')}
          rowLimitForPaginationHide={25}
          showPagination={true}
          disableColumnResize={true}
          defaultSortColumnName="projectName"
          primaryKey="projectId"
          checkBoxVisible={false}
          rowSelection={false}
          headerFilters
          serverSideEvents={serverSideEvents}
          disableColumnFilter
          defaultItemsPerPage={filterState?.paginationState.pageSize}
          filterDebounceMs={500}
          totalRowCount={totalRowCount}
          enableServerSide={true}
          filterState={filterState}
          noRowsOverlay={AurDataGridNoRowsOverlay}
          handleRowClick={(params: GridRowParams) =>
            handleRowClick(params.id.toString())
          }
          enableRowClickOnEnterPress={true}
        />
      </Box>
    </Box>
  );
};

export default AurProjectListContainer;
